import { EventBus, type BadgePayload, type WindowId } from './EventBus';

export interface WindowManagerOptions {
  bus: EventBus;
}

const DEFAULT_BADGE: BadgePayload = { label: '', count: 0, pulse: false, iconColor: 0x000000 };

export class WindowManager {
  private readonly bus: EventBus;
  private readonly registered = new Set<WindowId>();
  private readonly minimizedOrder: WindowId[] = [];
  private readonly focusStack: WindowId[] = [];
  private readonly badges = new Map<WindowId, BadgePayload>();

  constructor(options: WindowManagerOptions) {
    this.bus = options.bus;
    this.bus.on('window:minimize-requested', this.onMinimizeRequested);
    this.bus.on('window:restore-requested', this.onRestoreRequested);
    this.bus.on('window:focus-requested', this.onFocusRequested);
    this.bus.on('window:badge-changed', this.onBadgeChanged);
  }

  register(id: WindowId): void {
    this.registered.add(id);
  }

  getRegistered(): readonly WindowId[] {
    return [...this.registered];
  }

  isMinimized(id: WindowId): boolean {
    return this.minimizedOrder.includes(id);
  }

  getMinimizedOrder(): readonly WindowId[] {
    return [...this.minimizedOrder];
  }

  getFocusStack(): readonly WindowId[] {
    return [...this.focusStack];
  }

  getBadge(id: WindowId): BadgePayload {
    return this.badges.get(id) ?? { ...DEFAULT_BADGE };
  }

  destroy(): void {
    this.bus.off('window:minimize-requested', this.onMinimizeRequested);
    this.bus.off('window:restore-requested', this.onRestoreRequested);
    this.bus.off('window:focus-requested', this.onFocusRequested);
    this.bus.off('window:badge-changed', this.onBadgeChanged);
  }

  private readonly onMinimizeRequested = (p: { windowId: WindowId }): void => {
    if (!this.registered.has(p.windowId)) return;
    if (this.minimizedOrder.includes(p.windowId)) return;
    this.minimizedOrder.push(p.windowId);
    const order = this.minimizedOrder.length - 1;
    this.bus.emit('window:minimized', { windowId: p.windowId, order });
  };

  private readonly onRestoreRequested = (p: { windowId: WindowId }): void => {
    const idx = this.minimizedOrder.indexOf(p.windowId);
    if (idx === -1) return;
    this.minimizedOrder.splice(idx, 1);
    this.bus.emit('window:restored', { windowId: p.windowId });
  };

  private readonly onFocusRequested = (p: { windowId: WindowId }): void => {
    if (!this.registered.has(p.windowId)) return;
    const top = this.focusStack[this.focusStack.length - 1];
    if (top === p.windowId) return;
    const idx = this.focusStack.indexOf(p.windowId);
    if (idx !== -1) this.focusStack.splice(idx, 1);
    this.focusStack.push(p.windowId);
    this.bus.emit('window:focus-changed', {
      focused: p.windowId,
      stack: [...this.focusStack],
    });
  };

  private readonly onBadgeChanged = (p: { windowId: WindowId; badge: BadgePayload }): void => {
    this.badges.set(p.windowId, p.badge);
  };
}
